import { useEffect, useState } from 'react';
import { listStaffCustomers, type StaffCustomer } from '../api/customers.js';

// #516 — id → company name, for every staff role.
//
// A case carries its owner as a slug (`acme-corp`), and a slug in a header is
// the kind of thing that gets misread across two similar tenants — which is the
// exact error #516 exists to prevent. The names live in the customers table,
// and until #514 the only route to them was admin-only, so an analyst could not
// resolve one at all. `GET /api/v1/customers` is the minimal staff-readable
// projection that fixes that.
//
// ONE FETCH PER SESSION, shared. The Cases list renders the resolver once per
// row and the case header once per case; without a shared cache that is a
// request per mount, for a list that changes about as often as the customer
// roster does. The promise itself is the cache, so concurrent mounts share the
// one in-flight request rather than racing.
//
// A FAILED LOAD IS NOT FATAL and deliberately not retried in a loop: every
// caller falls back to rendering the raw id, which is what the rest of the
// staff portal shows anyway (Intake, TN changes, company changes). Losing the
// name degrades the display; it never blanks the attribution. The cache is
// cleared on failure so a later mount tries again.
let inFlight: Promise<StaffCustomer[]> | null = null;

export function loadCustomerDirectory(): Promise<StaffCustomer[]> {
  inFlight ??= listStaffCustomers().catch((err) => {
    inFlight = null;
    throw err;
  });
  return inFlight;
}

/** Test seam: drops the shared cache so a suite can start from nothing. */
export function resetCustomerDirectory(): void {
  inFlight = null;
}

export interface CustomerDirectory {
  /** The company name for an id, or null when unknown or not loaded yet. */
  nameOf: (customerId: string | null | undefined) => string | null;
  all: StaffCustomer[];
  loaded: boolean;
}

export function useCustomerDirectory(): CustomerDirectory {
  const [all, setAll] = useState<StaffCustomer[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let live = true;
    loadCustomerDirectory()
      .then((items) => { if (live) { setAll(items); setLoaded(true); } })
      // Swallowed on purpose — see the header. Callers render the raw id.
      .catch(() => { if (live) setLoaded(true); });
    return () => { live = false; };
  }, []);

  return {
    all,
    loaded,
    nameOf: (customerId) => {
      if (!customerId) return null;
      return all.find((c) => c.customerId === customerId)?.name ?? null;
    },
  };
}
